import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lessonRunsApi } from '../../../services/apiV2';
import { hasStepContext, useHelpStepContext } from '../../../hooks/v2/useHelpStepContext';
import { LessonStep } from '../../../types/v2';
import { Button } from '../../ui/button';

type HelpAction = 'teacher' | 'system' | 'explain' | 'example' | 'hint' | 'step_breakdown';
type HelpState = 'teacher' | 'system' | 'default';
type AssistMode = 'simplify' | 'example' | 'hint' | 'step_breakdown';
type TeacherSuggestionType = 'simplify' | 'example' | 'step_breakdown';
type HelpSurfaceMode = 'auto' | 'full' | 'limited' | 'fallback';

const HELP_SURFACE_REFRESH_MS = 15000;

interface HelpSurfaceApi {
  getMyState?: () => Promise<any>;
  getSuggestions?: () => Promise<any>;
  assist?: (payload: { step_id: number; assist_mode: AssistMode }) => Promise<any>;
  followup?: (payload: {
    step_id: number;
    question: string;
    assist_mode?: AssistMode;
    prior_help_title?: string;
    prior_help_content?: string;
  }) => Promise<any>;
}

interface HelpTabProps {
  runId?: number | null;
  stepKey?: string | number | null;
  onGoToLesson?: () => void;
  currentStep?: LessonStep | null;
  runStatus?: string | null;
  supportMode?: HelpSurfaceMode;
  limitedMessage?: string | null;
  hasStepContextOverride?: boolean | null;
  surfaceApi?: HelpSurfaceApi;
}

const FALLBACK_RESULTS: Record<HelpAction, string> = {
  teacher: 'Try one example first. Copy the pattern once, then try your own.',
  system: 'Start with the short version. Say the main idea, then add one detail.',
  explain: 'Short version: name the main idea first, then add one detail.',
  example: 'Example: look at one model, copy the pattern once, then try the next part yourself.',
  hint: 'Hint: start with one clear next move instead of trying to do the whole step at once.',
  step_breakdown: 'Break it down like this:\n1. Name what the step is asking for.\n2. Use one detail from the prompt or your work.\n3. Explain how that detail helps you move forward.',
};

const FOLLOW_UP_ACTIONS = new Set<HelpAction>(['hint', 'step_breakdown']);
const PRIMARY_RESULT_ACTIONS = new Set<HelpAction>(['teacher', 'system', 'explain', 'example']);

const getPrimaryState = (teacherSuggestion?: string | null, systemSuggestion?: string | null): HelpState => {
  if (String(teacherSuggestion || '').trim()) return 'teacher';
  if (String(systemSuggestion || '').trim()) return 'system';
  return 'default';
};

const getTeacherSuggestionType = (payload: any): TeacherSuggestionType | null => {
  const rawValue = String(payload?.teacher_action_state?.last_support_suggestion || '').trim().toLowerCase();
  if (rawValue === 'simplify' || rawValue === 'example' || rawValue === 'step_breakdown') {
    return rawValue;
  }
  return null;
};

const getAdaptiveSuggestionType = (payload: any): AssistMode | null => {
  const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
  const firstType = String(suggestions[0]?.type || '').trim().toLowerCase();
  if (firstType === 'simplify' || firstType === 'example' || firstType === 'hint' || firstType === 'step_breakdown') {
    return firstType;
  }
  return null;
};

const mapTeacherSuggestion = (value: TeacherSuggestionType | null): { text: string; mode: AssistMode } | null => {
  if (value === 'example') {
    return { text: 'Try an example', mode: 'example' };
  }
  if (value === 'step_breakdown') {
    return { text: 'Break it into steps', mode: 'step_breakdown' };
  }
  if (value === 'simplify') {
    return { text: 'Explain it differently', mode: 'simplify' };
  }
  return null;
};

const mapAdaptiveSuggestion = (value: AssistMode | null): { text: string; mode: AssistMode } | null => {
  if (value === 'example') {
    return { text: 'Try an example', mode: 'example' };
  }
  if (value === 'step_breakdown') {
    return { text: 'Break it into steps', mode: 'step_breakdown' };
  }
  if (value === 'hint') {
    return { text: 'Try a hint', mode: 'hint' };
  }
  if (value === 'simplify') {
    return { text: 'Try a simpler explanation', mode: 'simplify' };
  }
  return null;
};

export const HelpTab: React.FC<HelpTabProps> = ({
  runId,
  stepKey,
  onGoToLesson,
  currentStep,
  runStatus,
  supportMode = 'auto',
  limitedMessage,
  hasStepContextOverride = null,
  surfaceApi,
}) => {
  const [selectedAction, setSelectedAction] = useState<HelpAction | null>(null);
  const [resultText, setResultText] = useState('');
  const [resultTitle, setResultTitle] = useState('');
  const [errorText, setErrorText] = useState('');
  const [teacherSuggestion, setTeacherSuggestion] = useState<string | null>(null);
  const [teacherMode, setTeacherMode] = useState<AssistMode | null>(null);
  const [systemSuggestion, setSystemSuggestion] = useState<string | null>(null);
  const [systemMode, setSystemMode] = useState<AssistMode | null>(null);
  const [loadingSurface, setLoadingSurface] = useState(false);
  const [runningAction, setRunningAction] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState('');
  const [followupReply, setFollowupReply] = useState('');
  const [followupReplyTitle, setFollowupReplyTitle] = useState('');
  const [followupError, setFollowupError] = useState('');
  const [runningFollowup, setRunningFollowup] = useState(false);
  const loadRequestIdRef = useRef(0);
  const supportSurfaceRequestInFlightRef = useRef(false);
  const actionRequestIdRef = useRef(0);
  const followupRequestIdRef = useRef(0);
  const lastStepIdRef = useRef<number | null>(null);
  const surfaceApiRef = useRef(surfaceApi);
  const { context, hasStepContext: detectedStepContext, loading: loadingContext } = useHelpStepContext(
    currentStep ? undefined : runId,
  );
  const resolvedContext = useMemo(() => ({
    runId: runId ?? context?.runId ?? null,
    stepId: currentStep?.id ?? context?.stepId ?? null,
    step: currentStep ?? context?.step ?? null,
    runStatus: String(runStatus ?? context?.runStatus ?? '').trim().toLowerCase(),
  }), [context?.runId, context?.runStatus, context?.step, context?.stepId, currentStep, runId, runStatus]);
  const resolvedHasStepContext = hasStepContextOverride !== null
    ? Boolean(hasStepContextOverride)
    : currentStep
      ? hasStepContext(resolvedContext)
      : detectedStepContext;
  const currentStepId = resolvedContext.stepId ?? null;
  const resolvedSurfaceMode = useMemo<HelpSurfaceMode>(() => {
    if (supportMode !== 'auto') return supportMode;
    return resolvedHasStepContext ? 'full' : 'fallback';
  }, [resolvedHasStepContext, supportMode]);

  useEffect(() => {
    surfaceApiRef.current = surfaceApi;
  }, [surfaceApi]);

  const primaryState = useMemo(
    () => getPrimaryState(teacherSuggestion, systemSuggestion),
    [systemSuggestion, teacherSuggestion],
  );

  const resetHelp = useCallback(() => {
    setSelectedAction(null);
    setResultText('');
    setResultTitle('');
    setErrorText('');
    setRunningAction(false);
    setFollowupQuestion('');
    setFollowupReply('');
    setFollowupReplyTitle('');
    setFollowupError('');
    setRunningFollowup(false);
  }, []);

  const resetSurface = useCallback(() => {
    setTeacherSuggestion(null);
    setTeacherMode(null);
    setSystemSuggestion(null);
    setSystemMode(null);
    setLoadingSurface(false);
  }, []);

  const loadSupportSurface = useCallback(async () => {
    if (supportSurfaceRequestInFlightRef.current) return;
    supportSurfaceRequestInFlightRef.current = true;
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    const currentSurfaceApi = surfaceApiRef.current;
    const getMyState = currentSurfaceApi?.getMyState ?? (runId ? (() => lessonRunsApi.myState(runId)) : null);
    const getSuggestions = currentSurfaceApi?.getSuggestions ?? (runId ? (() => lessonRunsApi.udlSuggestions(runId)) : null);

    if (!getSuggestions || resolvedSurfaceMode !== 'full' || !resolvedHasStepContext || !currentStepId) {
      supportSurfaceRequestInFlightRef.current = false;
      resetSurface();
      resetHelp();
      lastStepIdRef.current = null;
      return;
    }

    setLoadingSurface(true);

    try {
      const [myStatePayload, suggestionsPayload] = await Promise.all([
        getMyState ? getMyState() : Promise.resolve({ teacher_action_state: {} }),
        getSuggestions(),
      ]);

      if (loadRequestIdRef.current !== requestId) return;

      const teacherSuggestionType = getTeacherSuggestionType(myStatePayload);
      const adaptiveSuggestionType = getAdaptiveSuggestionType(suggestionsPayload);
      const teacherSuggestionConfig = mapTeacherSuggestion(teacherSuggestionType);
      const adaptiveSuggestionConfig = mapAdaptiveSuggestion(adaptiveSuggestionType);

      setTeacherSuggestion(teacherSuggestionConfig?.text || null);
      setTeacherMode(teacherSuggestionConfig?.mode || null);
      setSystemSuggestion(adaptiveSuggestionConfig?.text || null);
      setSystemMode(adaptiveSuggestionConfig?.mode || null);

      if (lastStepIdRef.current !== currentStepId) {
        resetHelp();
      }
      lastStepIdRef.current = currentStepId;
    } catch {
      if (loadRequestIdRef.current !== requestId) return;
      setTeacherSuggestion(null);
      setTeacherMode(null);
      setSystemSuggestion(null);
      setSystemMode(null);
    } finally {
      supportSurfaceRequestInFlightRef.current = false;
      if (loadRequestIdRef.current === requestId) {
        setLoadingSurface(false);
      }
    }
  }, [currentStepId, resetHelp, resetSurface, resolvedHasStepContext, resolvedSurfaceMode, runId]);

  useEffect(() => {
    resetHelp();
  }, [resetHelp, stepKey]);

  useEffect(() => {
    void loadSupportSurface();
    if (resolvedSurfaceMode !== 'full' || !resolvedHasStepContext || !currentStepId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadSupportSurface();
    }, HELP_SURFACE_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [currentStepId, loadSupportSurface, resolvedHasStepContext, resolvedSurfaceMode, runId]);

  const runHelpAction = (action: HelpAction) => {
    const requestId = actionRequestIdRef.current + 1;
    actionRequestIdRef.current = requestId;
    const currentSurfaceApi = surfaceApiRef.current;
    const assistRequester = currentSurfaceApi?.assist ?? (runId ? ((payload: { step_id: number; assist_mode: AssistMode }) => lessonRunsApi.udlAssist(runId, payload)) : null);
    setSelectedAction(action);
    setResultText('');
    setResultTitle('');
    setErrorText('');
    setRunningAction(true);

    const fallbackResult = String(FALLBACK_RESULTS[action] || '').trim();
    const assistMode = (
      action === 'teacher'
        ? teacherMode
        : action === 'system'
          ? systemMode
        : action === 'hint'
          ? 'hint'
        : action === 'step_breakdown'
          ? 'step_breakdown'
        : action === 'example'
            ? 'example'
            : 'simplify'
    ) as AssistMode | null;

    if (!assistRequester || !currentStepId || !assistMode) {
      setResultText(fallbackResult);
      setRunningAction(false);
      return;
    }

    void assistRequester({
      step_id: currentStepId,
      assist_mode: assistMode,
    }).then((payload) => {
      if (actionRequestIdRef.current !== requestId) return;
      const nextResult = String(payload?.content || '').trim();
      if (!nextResult) {
        throw new Error('empty_help_result');
      }
      setResultTitle(String(payload?.title || '').trim());
      setResultText(nextResult);
      setErrorText('');
      setRunningAction(false);
      setFollowupQuestion('');
      setFollowupReply('');
      setFollowupReplyTitle('');
      setFollowupError('');
    }).catch(() => {
      if (actionRequestIdRef.current !== requestId) return;
      setResultText('');
      setResultTitle('');
      setErrorText('That did not work. Try again.');
      setRunningAction(false);
      setFollowupQuestion('');
      setFollowupReply('');
      setFollowupReplyTitle('');
      setFollowupError('');
    });
  };

  const runHelpFollowup = () => {
    const trimmedQuestion = String(followupQuestion || '').trim();
    if (!trimmedQuestion) {
      setFollowupError('Ask a short question first.');
      return;
    }

    const requestId = followupRequestIdRef.current + 1;
    followupRequestIdRef.current = requestId;
    const currentSurfaceApi = surfaceApiRef.current;
    const followupRequester = currentSurfaceApi?.followup ?? (
      runId
        ? ((payload: {
            step_id: number;
            question: string;
            assist_mode?: AssistMode;
            prior_help_title?: string;
            prior_help_content?: string;
          }) => lessonRunsApi.udlFollowup(runId, payload))
        : null
    );

    let assistMode: AssistMode | undefined;
    if (selectedAction === 'teacher') {
      assistMode = teacherMode || undefined;
    } else if (selectedAction === 'system') {
      assistMode = systemMode || undefined;
    } else if (selectedAction === 'example') {
      assistMode = 'example';
    } else if (selectedAction === 'hint') {
      assistMode = 'hint';
    } else if (selectedAction === 'step_breakdown') {
      assistMode = 'step_breakdown';
    } else if (selectedAction === 'explain') {
      assistMode = 'simplify';
    }

    if (!followupRequester || !currentStepId || !resultText) {
      setFollowupError('That did not work. Try again.');
      return;
    }

    setFollowupError('');
    setRunningFollowup(true);
    void followupRequester({
      step_id: currentStepId,
      question: trimmedQuestion,
      assist_mode: assistMode,
      prior_help_title: resultTitle || '',
      prior_help_content: resultText,
    }).then((payload) => {
      if (followupRequestIdRef.current !== requestId) return;
      const nextReply = String(payload?.content || '').trim();
      if (!nextReply) {
        throw new Error('empty_followup_result');
      }
      setFollowupReplyTitle(String(payload?.title || '').trim());
      setFollowupReply(nextReply);
      setFollowupError('');
      setRunningFollowup(false);
    }).catch(() => {
      if (followupRequestIdRef.current !== requestId) return;
      setFollowupReply('');
      setFollowupReplyTitle('');
      setFollowupError('That did not work. Try again.');
      setRunningFollowup(false);
    });
  };

  const primaryLabel = primaryState === 'teacher'
    ? `Teacher suggests: ${String(teacherSuggestion || '').trim()}`
    : primaryState === 'system'
      ? `Suggestion: ${String(systemSuggestion || '').trim()}`
      : 'Need help?';

  const usedFollowUp = selectedAction ? FOLLOW_UP_ACTIONS.has(selectedAction) : false;
  const canShowFollowUps = Boolean(resultText && selectedAction && PRIMARY_RESULT_ACTIONS.has(selectedAction) && !errorText);
  const followupLocked = Boolean(followupReply);

  const actionButtons = (() => {
    if (followupLocked) {
      return [
        <Button key="reset-followup-reply" type="button" variant="secondary" size="sm" onClick={resetHelp}>
          Reset
        </Button>,
      ];
    }

    if (usedFollowUp) {
      return [
        <Button key="reset-followup-help" type="button" variant="secondary" size="sm" onClick={resetHelp}>
          Reset
        </Button>,
      ];
    }

    if (primaryState === 'teacher') {
      if (selectedAction || errorText) {
        if (canShowFollowUps) {
          return [
            <Button key="breakdown-followup" type="button" size="sm" onClick={() => runHelpAction('step_breakdown')} disabled={runningAction || loadingSurface}>
              Break it down
            </Button>,
            <Button key="hint-followup" type="button" variant="secondary" size="sm" onClick={() => runHelpAction('hint')} disabled={runningAction || loadingSurface}>
              Give me a hint
            </Button>,
          ];
        }
        return [
          <Button key="reset-teacher-help" type="button" variant="secondary" size="sm" onClick={resetHelp}>
            Reset
          </Button>,
        ];
      }
      return [
        <Button key="teacher-help" type="button" size="sm" onClick={() => runHelpAction('teacher')} disabled={runningAction || loadingSurface}>
          Try it
        </Button>,
      ];
    }

    if (primaryState === 'system') {
      if (selectedAction || errorText) {
        if (canShowFollowUps) {
          return [
            <Button key="breakdown-followup" type="button" size="sm" onClick={() => runHelpAction('step_breakdown')} disabled={runningAction || loadingSurface}>
              Break it down
            </Button>,
            <Button key="hint-followup" type="button" variant="secondary" size="sm" onClick={() => runHelpAction('hint')} disabled={runningAction || loadingSurface}>
              Give me a hint
            </Button>,
          ];
        }
        return [
          <Button key="reset-system-help" type="button" variant="secondary" size="sm" onClick={resetHelp}>
            Reset
          </Button>,
        ];
      }
      return [
        <Button key="system-help" type="button" size="sm" onClick={() => runHelpAction('system')} disabled={runningAction || loadingSurface}>
          Try it
        </Button>,
      ];
    }

    if (selectedAction === 'explain' || selectedAction === 'example' || errorText) {
      if (canShowFollowUps) {
        return [
          <Button key="breakdown-followup" type="button" size="sm" onClick={() => runHelpAction('step_breakdown')} disabled={runningAction || loadingSurface}>
            Break it down
          </Button>,
          <Button key="hint-followup" type="button" variant="secondary" size="sm" onClick={() => runHelpAction('hint')} disabled={runningAction || loadingSurface}>
            Give me a hint
          </Button>,
        ];
      }
      return [
        <Button key="reset-default-help" type="button" variant="secondary" size="sm" onClick={resetHelp}>
          Reset
        </Button>,
      ];
    }

    return [
      <Button key="default-explain-help" type="button" size="sm" onClick={() => runHelpAction('explain')} disabled={runningAction || loadingSurface}>
        Explain it differently
      </Button>,
      <Button key="default-example-help" type="button" variant="secondary" size="sm" onClick={() => runHelpAction('example')} disabled={runningAction || loadingSurface}>
        Show an example
      </Button>,
    ];
  })();

  if (resolvedSurfaceMode === 'limited') {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4">
          <p className="text-sm font-semibold text-text">{limitedMessage || 'Help is off for this step.'}</p>
        </div>
      </div>
    );
  }

  if (!resolvedHasStepContext || resolvedSurfaceMode === 'fallback') {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4">
          <p className="text-sm font-semibold text-text">Help appears when you are working on a lesson step.</p>
          {runId && loadingContext && !currentStep ? (
            <div className="mt-3 min-h-[44px] rounded-lg border border-border bg-surface px-4 py-3">
              <p className="text-sm text-muted">Checking your current step...</p>
            </div>
          ) : null}
          {onGoToLesson ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={onGoToLesson}>
                Go to your lesson
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4">
        <p className="text-sm font-semibold text-text">{primaryLabel}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {actionButtons}
        </div>
        <div className="mt-3 min-h-[88px] rounded-lg border border-border bg-surface px-4 py-3">
          {errorText ? (
            <div className="space-y-2">
              <p className="text-sm text-danger">{errorText}</p>
              <button
                type="button"
                className="text-xs font-semibold text-muted underline-offset-2 hover:underline"
                onClick={resetHelp}
              >
                Reset help
              </button>
            </div>
          ) : resultText ? (
            <div className="space-y-2">
              {resultTitle ? (
                <p className="text-sm font-semibold text-text">{resultTitle}</p>
              ) : null}
              <p className="whitespace-pre-wrap text-sm text-text">{resultText}</p>
              {!usedFollowUp ? null : (
                <button
                  type="button"
                  className="text-xs font-semibold text-muted underline-offset-2 hover:underline"
                  onClick={resetHelp}
                >
                  Reset help
                </button>
              )}
              {!followupReply ? (
                <div className="space-y-2 border-t border-border pt-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor={`help-followup-${currentStepId}`}>
                    Ask about this step
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      id={`help-followup-${currentStepId}`}
                      type="text"
                      maxLength={180}
                      value={followupQuestion}
                      onChange={(event) => setFollowupQuestion(event.target.value)}
                      placeholder="Ask a short question"
                      className="h-9 flex-1 rounded-md border border-border bg-background px-3 text-sm text-text outline-none ring-offset-background placeholder:text-muted focus-visible:ring-2 focus-visible:ring-ring"
                      disabled={runningFollowup}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={runHelpFollowup}
                      disabled={runningFollowup}
                    >
                      Ask
                    </Button>
                  </div>
                  {followupError ? (
                    <p className="text-sm text-danger">{followupError}</p>
                  ) : null}
                  {runningFollowup ? (
                    <p className="text-sm text-muted">Thinking about your question...</p>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 border-t border-border pt-3">
                  {followupReplyTitle ? (
                    <p className="text-sm font-semibold text-text">{followupReplyTitle}</p>
                  ) : null}
                  <p className="whitespace-pre-wrap text-sm text-text">{followupReply}</p>
                  <button
                    type="button"
                    className="text-xs font-semibold text-muted underline-offset-2 hover:underline"
                    onClick={resetHelp}
                  >
                    Reset help
                  </button>
                </div>
              )}
            </div>
          ) : runningAction || loadingSurface ? (
            <p className="text-sm text-muted">Getting help...</p>
          ) : (
            <p className="text-sm text-muted">Pick one quick next step.</p>
          )}
        </div>
      </div>
    </div>
  );
};
